import type { ReactElement } from "react";
import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { Button, Callout, Stack, Surface, Text } from "../ui-kit";
import { readConfig } from "../config";
import { PageHeader } from "../shell/PageHeader";
import { useSignInError } from "./AuthProvider";

/**
 * The single unauthenticated screen (ISSUE-329 Deliverable C, step 8).
 * `RequireAuth` renders this instead of a data view for a signed-out
 * visitor. A failed/denied sign-in renders a plain message built from
 * MSAL's short `errorCode` — never the raw error object or any token
 * content (ISSUE-329 §Log statements).
 */
export function SignInView(): ReactElement {
  const { instance } = useMsal();
  const signInError = useSignInError();

  const handleSignIn = useCallback(() => {
    const config = readConfig();
    if (!config.ok) {
      // RequireAuth never renders this view with an incomplete
      // configuration, but the button stays inert rather than trusting that.
      return;
    }
    // Authorization code + PKCE (ADR-055 §4) — `loginRedirect` derives the
    // PKCE verifier/challenge itself; requesting the admin API's own scope,
    // never the ID token.
    void instance.loginRedirect({ scopes: [config.config.entraApiScope] });
  }, [instance]);

  return (
    <Stack gap={6}>
      <PageHeader
        title="Sign in"
        description="Sign in with your AI Consultant operator account to view this portal."
      />
      <Surface as="section" elevation="raised" aria-label="Sign in">
        <Stack gap={4} align="start">
          {signInError !== null ? (
            <Callout tone="danger" title="Sign-in was not completed">
              <Text variant="body">
                Try again, or contact an administrator if this keeps happening. (Reference:{" "}
                {signInError})
              </Text>
            </Callout>
          ) : null}
          <Button onClick={handleSignIn}>Sign in</Button>
        </Stack>
      </Surface>
    </Stack>
  );
}
